'use client';

import Image from 'next/image';
import { ReactNode, useState } from 'react';

interface BaseAuctionCardProps {
  id: string;
  title: string;
  location: string;
  image: string;
  isCompleted?: boolean;
  isUpcoming?: boolean;
  children?: ReactNode;
  imageOverlay?: ReactNode;
  favoriteButton?: ReactNode;
  actionButton?: ReactNode;
  className?: string;
  priority?: boolean;
}

export default function BaseAuctionCard({
  title,
  location,
  image,
  isCompleted = false,
  isUpcoming = false,
  children,
  imageOverlay,
  favoriteButton,
  actionButton,
  className = '',
  priority = false
}: BaseAuctionCardProps) {
  const [imageError, setImageError] = useState(false);

  return (
    <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full w-full ${
      isCompleted 
        ? 'opacity-70' 
        : !isUpcoming 
          ? 'hover:scale-105 hover:shadow-xl hover:shadow-red-500/20' 
          : 'hover:scale-105 hover:shadow-xl'
    } ${className}`}>
      {/* Image Container */}
      <div className="relative h-64">
        {image && !imageError ? (
          <Image
            src={image}
            alt={`${title} - ${location || 'Gayrimenkul'} müzayede görseli`}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
            className="object-cover"
            priority={priority}
            fetchPriority={priority ? "high" : "auto"}
            quality={70}
            loading={priority ? undefined : "lazy"}
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
              </svg>
            </div>
          </div>
        )}
        
        {/* Image Overlay */}
        {imageOverlay}
        
        {/* Favorite Button */}
        {favoriteButton}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-grow">
        <h4 className={`font-bold text-gray-900 mb-2 line-clamp-2 font-serif ${isCompleted ? 'text-xl' : 'text-xl'}`}>
          {title}
        </h4>
        {!isCompleted && <p className="text-gray-600 mb-4">{location}</p>}
        
        {children}
        
        {/* Action Button */}
        {actionButton}
      </div>
    </div>
  );
}
